/**
 * 系统管理后台接口（路径与字段严格对齐 apps/api/src/modules/admin/admin.routes.ts）。
 *
 * ⚠️ 这里**只允许**定义账号元数据 / 部署实例相关的接口。
 * 任何"读学生、读打卡、读积分"的接口都不该出现在这个文件里 ——
 * 那是家庭数据，管理员没有权限看（见方案 2B.1）。
 */
import { api } from './client';

// ---------------------------------------------------------------- 类型

/** 账号元数据。**没有**任何学生 / 打卡 / 积分字段，这是刻意的。 */
export interface AdminUserRow {
  id: string;
  role: 'parent' | 'student' | 'system_admin';
  username: string;
  nickname: string;
  email: string | null;
  emailVerifiedAt: string | null;
  totpEnabledAt: string | null;
  lockedUntil: string | null;
  failedAttempts: number;
  lastLoginAt: string | null;
  createdAt: string;
  deletedAt: string | null;
}

export interface AdminUserListResponse {
  users: AdminUserRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminOverview {
  accounts: { parents: number; students: number; admins: number; disabled: number; total: number };
  families: number;
  counts: {
    students: number;
    nodes: number;
    habits: number;
    /** 打卡**记录**数（`checkin` 行数）—— 看板上的「打卡记录」；不是打卡任务数（那是 `habits`）。 */
    checkins: number;
    checkinsDone: number;
    checkinsLast7Days: number;
  };
  database: { sizeBytes: number | null; sizeHuman: string | null };
  runtime: {
    nodeVersion: string;
    platform: string;
    arch: string;
    hostname: string;
    kernel: string;
    cpuCount: number;
    loadAverage: number[];
    uptimeSeconds: number;
    processUptimeSeconds: number;
    memory: { totalBytes: number; freeBytes: number; usedPercent: number };
    processMemory: { rssBytes: number; heapUsedBytes: number; heapTotalBytes: number };
  };
  disk: {
    available: boolean;
    path?: string;
    totalBytes?: number;
    freeBytes?: number;
    availableBytes?: number;
    reason?: string;
  };
  certificate: {
    available: boolean;
    path?: string;
    subject?: string;
    issuer?: string;
    validFrom?: string;
    validTo?: string;
    daysRemaining?: number;
    reason?: string;
  };
  containers: { available: boolean; reason: string; manualCommands: string[] };
}

export interface AdminSettings {
  familyName: string;
  timezone: string;
  registrationOpen: boolean;
  requireInviteCode: boolean;
  makeupEnabled: boolean;
  makeupPointsFactor: number;
  updatedAt: string;
}

export interface AdminAuditRow {
  id: string;
  actorType: 'parent' | 'student' | 'system';
  actorId: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  ip: string | null;
  createdAt: string;
  before: unknown;
  after: unknown;
}

export interface AdminAuditListResponse {
  rows: AdminAuditRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminBackupStatus {
  configured: boolean;
  reason: string | null;
  markerFile: string | null;
  lastSuccessAt: string | null;
  ageHours: number | null;
  stale: boolean | null;
  stalenessThresholdHours: number;
  sizeBytes: number | null;
  sizeHuman: string | null;
  node: {
    configured: boolean;
    url: string | null;
    reachable: boolean | null;
    status: number | null;
    latencyMs: number | null;
    reason: string | null;
  };
  readonly: true;
  manualCommands: string[];
}

// ---------------------------------------------------------------- 接口

export interface AdminUserQuery {
  page?: number;
  pageSize?: number;
  q?: string;
  role?: AdminUserRow['role'];
  status?: 'active' | 'disabled';
}

export async function getOverview(): Promise<AdminOverview> {
  return api.get<AdminOverview>('/admin/overview');
}

export async function listUsers(query: AdminUserQuery = {}): Promise<AdminUserListResponse> {
  const params: Record<string, string | number | undefined> = {
    page: query.page,
    pageSize: query.pageSize,
    q: query.q,
    role: query.role,
    status: query.status,
  };
  return api.get<AdminUserListResponse>(
    '/admin/users',
    params as Record<string, string | number | null | undefined>,
  );
}

export async function revokeUserSessions(id: string): Promise<{ revokedSessions: number }> {
  return api.post<{ revokedSessions: number }>(`/admin/users/${id}/revoke-sessions`);
}

export async function setUserStatus(
  id: string,
  disabled: boolean,
): Promise<{ disabled: boolean; revokedSessions: number }> {
  return api.patch<{ disabled: boolean; revokedSessions: number }>(`/admin/users/${id}/status`, {
    disabled,
  });
}

export async function getSettings(): Promise<AdminSettings> {
  return api.get<AdminSettings>('/admin/settings');
}

export async function updateSettings(patch: {
  familyName?: string;
  timezone?: string;
  registrationOpen?: boolean;
  requireInviteCode?: boolean;
}): Promise<AdminSettings> {
  return api.patch<AdminSettings>('/admin/settings', patch);
}

export async function createInviteCode(ttlMinutes: number): Promise<{
  code: string;
  ttlMinutes: number;
  expiresAt: string;
  hint: string;
}> {
  return api.post('/admin/invite-code', { ttlMinutes });
}

export async function listAudit(query: {
  page?: number;
  pageSize?: number;
  action?: string;
  actorId?: string;
  from?: string;
  to?: string;
}): Promise<AdminAuditListResponse> {
  return api.get<AdminAuditListResponse>(
    '/admin/audit',
    query as Record<string, string | number | null | undefined>,
  );
}

export async function getBackupStatus(): Promise<AdminBackupStatus> {
  return api.get<AdminBackupStatus>('/admin/backup/status');
}

export async function sendMailTest(
  email: string,
  note = '',
): Promise<{ delivered: boolean; provider: string; to: string; elapsedMs: number }> {
  return api.post('/admin/mail-test', { email, note });
}

// ---------------------------------------------------------------- TOTP（复用 /auth，管理员必做）

export async function beginTotpSetup(): Promise<{
  secret: string;
  otpauthUrl: string;
  qrSvgDataUrl: string | null;
}> {
  return api.post('/auth/totp/setup');
}

export async function confirmTotpSetup(code: string): Promise<{ enabled: boolean }> {
  return api.post('/auth/totp/verify', { code });
}

/** 角色中文名（列表与确认文案共用，避免两处写法不一致）。 */
export const ADMIN_ROLE_LABEL: Record<AdminUserRow['role'], string> = {
  parent: '家长',
  student: '学生',
  system_admin: '系统管理员',
};
